<template>
  <n-config-provider abstract :theme-overrides="formThemeOverrides">
    <div ref="formContainer" class="signup-form flex flex-col">
      <div class="flex flex-col items-center gap-1 text-center">
        <div
          class="font-heading font-semibold text-[22px] leading-tight text-secondary"
        >
          {{ authStore.organization.name }}
        </div>
        <div class="text-sm text-muted">{{ subtitle }}</div>
      </div>
      <div class="mt-6">
        <n-form
          v-if="isCustomSignup"
          ref="customFormRef"
          :model="signupInfo"
          :rules="signupElementsRules"
          class="grid grid-cols-2 gap-x-3"
        >
          <n-form-item
            v-for="element in signupElements"
            :key="element.id"
            :path="element.attribute"
            :label="element.label"
            class="min-w-0"
            :class="[
              element.hidden ? 'hidden' : '',
              halfWidthAttributes.includes(element.attribute)
                ? 'col-span-1'
                : 'col-span-2',
            ]"
          >
            <n-input
              v-if="element.type === 'string'"
              @keydown.enter.prevent
              v-model:value="signupInfo[element.attribute]"
              :placeholder="element.placeholder"
              :input-props="inputProps(element)"
            />
            <div class="w-full" v-else-if="element.type === 'number'">
              <n-input-number
                @keydown.enter.prevent
                v-model:value="signupInfo[element.attribute]"
                :placeholder="element.placeholder"
              />
            </div>
            <vue-tel-input
              v-else-if="element.type === 'phoneNumber'"
              v-model="signupInfo[element.attribute]"
              :inputOptions="{ placeholder: element.placeholder }"
              :mode="'international'"
              :styleClasses="['w-full']"
              :defaultCountry="countryCode"
            />
            <n-select
              v-else-if="element.type === 'select'"
              v-model:value="signupInfo[element.attribute]"
              :placeholder="element.placeholder"
              :options="getFieldOptions(element)"
            />
            <signup-date-picker
              v-else
              v-model:value="signupInfo[element.attribute]"
              :placeholder="element.placeholder"
              :max="currentDate"
            />
          </n-form-item>
        </n-form>
        <legacy-customer-fields
          ref="legacyFormRef"
          v-else
          v-model:signupInfo="signupInfo"
          :predefined-rules="predefinedRules"
          :custom-fields="customFields"
        />
        <delivery-code-field
          v-if="$route.query.addPoints"
          v-model:value="deliveryCode"
        />
        <referral-code-field
          v-if="referralsEnabled"
          v-model:value="referralCode"
        />
      </div>
      <div
        class="mt-5 flex flex-col gap-3 text-[13.5px] leading-[1.45] text-secondary"
      >
        <div class="flex items-start gap-2.5">
          <n-checkbox
            v-model:checked="policyCheck"
            class="mt-px shrink-0"
            :class="{ 'signup-checkbox--error': policyError }"
          />
          <div class="cursor-pointer select-none" @click="handleLegalTextClick">
            <span data-legal="policy" v-html="$t('policy-accept')"></span>
            {{ " " }}
            <span data-legal="terms" v-html="$t('terms-accept')"></span>
          </div>
        </div>
        <div
          v-if="policyError"
          class="signup-policy-error -mt-1 pl-[30px] flex items-center gap-1 text-xs text-error"
        >
          <c-icon name="ErrorRound" size="14px" class="shrink-0" />
          {{ $t("must-accept-privacy-policy") }}
        </div>
        <div class="flex items-start gap-2.5">
          <n-checkbox
            v-model:checked="signupInfo.marketingCommunication"
            class="mt-px shrink-0"
          />
          <div
            class="cursor-pointer select-none"
            @click="
              signupInfo.marketingCommunication =
                !signupInfo.marketingCommunication
            "
          >
            {{
              signupPage.marketingCommunicationSentence ||
              $t("want-to-receive-promotions", {
                organizationName: authStore.organization.name,
              })
            }}
          </div>
        </div>
      </div>
      <n-button
        type="primary"
        size="large"
        block
        class="mt-6 font-heading"
        :loading="loading"
        @click="createCustomer"
        >{{ $t("sign-up") }}</n-button
      >
      <a
        href="https://comeback.es"
        target="_blank"
        rel="noopener"
        class="mt-5 self-center flex items-center gap-1.5 text-xs text-muted no-underline whitespace-nowrap hover:opacity-80"
      >
        <span>{{ $t("powered-by") }}</span>
        <img
          :src="palette.dark ? comebackLogoWhite : comebackLogoBlack"
          alt="Comeback"
          class="h-[22px]"
        />
      </a>
    </div>
  </n-config-provider>
  <c-loading-screen v-if="waitingNotificationsApproval" :show-spinner="false" />
</template>

<script>
import { mapStores, mapState } from "pinia";
import { authStore } from "@/stores/auth";
import api from "@/api.js";
import moment from "moment";
import { useNotification, useDialog } from "naive-ui";
import LegacyCustomerFields from "./LegacyCustomerFields.vue";
import DeliveryCodeField from "./DeliveryCodeField.vue";
import ReferralCodeField from "./ReferralCodeField.vue";
import SignupDatePicker from "./SignupDatePicker.vue";
import CIcon from "../../../core-ui/src/components/CIcon.vue";
import CLoadingScreen from "../../../core-ui/src/components/CLoadingScreen.vue";
import comebackLogoBlack from "../../../core-ui/src/assets/comeback-logo-black.png";
import comebackLogoWhite from "../../../core-ui/src/assets/comeback-logo-white.png";
import metaPixel from "@/metaPixel.js";
import googleConversion from "@/googleConversion.js";
import { rgba } from "@/palette.js";

const ERROR_COLOR = "#DB3D55";

export default {
  name: "SignupForm",
  data() {
    return {
      signupInfo: { customerCustomFields: {} },
      policyCheck: false,
      policyError: false,
      loading: false,
      notificationCreator: null,
      dialogCreator: null,
      deliveryCode: null,
      referralCode: null,
      giftCardId: null,
      waitingNotificationsApproval: false,
      comebackLogoBlack,
      comebackLogoWhite,
    };
  },
  components: {
    DeliveryCodeField,
    ReferralCodeField,
    LegacyCustomerFields,
    SignupDatePicker,
    CIcon,
    CLoadingScreen,
  },
  watch: {
    policyCheck(checked) {
      if (checked) this.policyError = false;
    },
  },
  mounted() {
    if (this.referralsEnabled && this.$route.query.referralCode) {
      this.referralCode = this.$route.query.referralCode;
    }
    if (this.$route.query.giftCardId) {
      this.giftCardId = this.$route.query.giftCardId;
    }
    this.notificationCreator = useNotification();
    this.dialogCreator = useDialog();
    this.fillDefaultValues();
  },
  computed: {
    ...mapStores(authStore),
    ...mapState(authStore, [
      "organizationConfig",
      "referralsEnabled",
      "signupElements",
      "signupPage",
      "locationsById",
      "customFields",
      "currentLocation",
      "serviceWorkerRegistration",
      "countryCode",
      "appointment",
      "reservation",
      "palette",
    ]),
    subtitle() {
      if (this.appointment)
        return this.$t("appointments.complete-info-to-book");
      if (this.reservation)
        return this.$t("reservations.complete-info-to-book");
      return this.$t("signup-subtitle");
    },
    halfWidthAttributes() {
      let visibleElements = (this.signupElements || []).filter(
        (e) => !e.hidden
      );
      let nameIndex = visibleElements.findIndex((e) => e.attribute === "name");
      return nameIndex !== -1 &&
        visibleElements[nameIndex + 1]?.attribute === "surname"
        ? ["name", "surname"]
        : [];
    },
    formThemeOverrides() {
      let p = this.palette;
      let border = `1.5px solid ${p.line}`;
      let focusBorder = `1.5px solid ${p.accent}`;
      let focusRing = `0 0 0 4px ${p.ring}`;
      let errorBorder = `1.5px solid ${ERROR_COLOR}`;
      let errorRing = `0 0 0 4px ${rgba(ERROR_COLOR, 0.15)}`;
      let field = {
        heightMedium: "48px",
        fontSizeMedium: "15px",
        borderRadius: "12px",
        color: p.field,
        textColor: p.text,
        placeholderColor: rgba(p.muted, 0.75),
        border,
        borderHover: border,
        borderFocus: focusBorder,
        boxShadowFocus: focusRing,
        borderError: errorBorder,
        borderHoverError: errorBorder,
        borderFocusError: errorBorder,
        boxShadowFocusError: errorRing,
      };
      let input = {
        ...field,
        paddingMedium: "0 14px",
        colorFocus: p.surface,
        colorFocusError: p.surface,
        caretColor: p.text,
        caretColorError: p.text,
        iconColor: p.muted,
      };
      return {
        Form: {
          labelTextColor: p.text,
          labelFontSizeTopMedium: "13px",
          labelFontWeight: "500",
          labelPaddingVertical: "0 0 6px 0",
          feedbackHeightMedium: "16px",
          feedbackFontSizeMedium: "12px",
          feedbackPadding: "6px 0 2px 0",
          feedbackTextColorError: ERROR_COLOR,
          blankHeightMedium: "48px",
        },
        Input: input,
        InputNumber: { peers: { Input: input } },
        Select: {
          peers: {
            InternalSelection: {
              ...field,
              paddingSingle: "0 32px 0 14px",
              colorActive: p.surface,
              colorActiveError: p.surface,
              arrowColor: p.muted,
              caretColor: p.text,
              borderActive: focusBorder,
              boxShadowActive: focusRing,
              borderActiveError: errorBorder,
              boxShadowActiveError: errorRing,
            },
          },
        },
        Checkbox: {
          sizeMedium: "20px",
          borderRadius: "6px",
          color: p.surface,
          colorChecked: p.accent,
          checkMarkColor: p.onAccent,
          border,
          borderChecked: focusBorder,
          borderFocus: focusBorder,
          boxShadowFocus: focusRing,
        },
        Button: {
          heightLarge: "52px",
          borderRadiusLarge: "14px",
          fontSizeLarge: "16px",
          fontWeight: "600",
        },
      };
    },
    currentDate() {
      return moment().subtract(1, "year").format("YYYY-MM-DD");
    },
    mappedSelectedCustomFields() {
      return Object.keys(this.signupInfo)
        .filter((k) => k.includes("customField."))
        .reduce((res, key) => {
          let customFieldId = key.split(".")[1];
          res[customFieldId] = this.signupInfo[key];
          return res;
        }, {});
    },
    isCustomSignup() {
      return (
        this.signupElements?.find((e) => e.attribute === "email") !== undefined
      );
    },
    deviceIsIphone() {
      return [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod",
      ].includes(navigator.platform);
    },
    formattedBirthDate() {
      if (!this.signupInfo.birthDate) {
        return undefined;
      } else {
        return moment(this.signupInfo.birthDate).format("yyyy-MM-DD");
      }
    },
    signupElementsRules() {
      return (
        this.signupElements?.reduce((acc, element) => {
          let predefinedValidator =
            this.predefinedRules[element.attribute]?.validator;

          if (element.mandatory) {
            let mandatoryValidator = (rule, value) => {
              if (!value || value === "") {
                return new Error(this.$t("mandatory-field-error"));
              }
              return true;
            };
            acc[element.attribute] = {
              validator: (rule, value) => {
                if (predefinedValidator) {
                  let predefinedValidation = predefinedValidator(rule, value);
                  if (predefinedValidation instanceof Error) {
                    return predefinedValidation;
                  } else {
                    return mandatoryValidator(rule, value);
                  }
                } else {
                  return mandatoryValidator(rule, value);
                }
              },
              trigger: "blur",
            };
          } else if (predefinedValidator) {
            // Apply predefined validators even for non-mandatory fields
            acc[element.attribute] = {
              validator: predefinedValidator,
              trigger: "blur",
            };
          }
          return acc;
        }, {}) || {}
      );
    },
    predefinedRules() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return {
        email: {
          validator: (rule, value) => {
            if (!value || !emailRegex.test(value.trim())) {
              return new Error(this.$t("email-must-be-valid"));
            }
            return true;
          },
          trigger: "blur",
        },
        phoneNumber: {
          validator: (rule, value) => {
            if (
              !this.organizationConfig.phoneNumberMandatory &&
              (!value || value === "")
            )
              return true;
            if (!this.$utils.parsePhoneNumberFromString(value)?.isPossible()) {
              return new Error(this.$t("phone-must-be-valid"));
            }
            return true;
          },
          trigger: "blur",
        },
      };
    },
    slugNameOrLocationId() {
      return this.$router.currentRoute.value.params.slugNameOrLocationId;
    },
  },
  methods: {
    inputProps(element) {
      let autocomplete = {
        name: "given-name",
        surname: "family-name",
        email: "email",
      }[element.attribute];
      return {
        autocomplete,
        inputmode: element.attribute === "email" ? "email" : undefined,
      };
    },
    handleLegalTextClick(event) {
      let legal = event.target.closest("[data-legal]")?.dataset.legal;
      if (legal && event.target.closest(".underline")) {
        legal === "policy" ? this.handleClickPolicy() : this.handleClickTerms();
      } else {
        this.policyCheck = !this.policyCheck;
      }
    },
    async scrollToFirstError() {
      await this.$nextTick();
      this.$refs.formContainer
        ?.querySelector(".n-form-item-blank--error, .signup-policy-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    fillDefaultValues() {
      this.signupElements?.forEach((element) => {
        if (element.defaultValue) {
          this.signupInfo[element.attribute] = element.defaultValue;
        }
      });
    },
    getFieldOptions(field) {
      if (field.attribute.includes("customField")) {
        let customField = this.customFields.find(
          (customField) => customField.id === field.attribute.split(".")[1]
        );
        return customField?.options.map((option) => ({
          label: option,
          value: option,
        }));
      }
    },
    redirectToLastapp(phoneNumber) {
      if (this.$route.query.lastappUrl) {
        let paramsComponent = "";
        if (phoneNumber) {
          paramsComponent = `&customerPhone=${phoneNumber}`;
        }
        window.location.replace(this.$route.query.lastappUrl + paramsComponent);
      }
    },
    handleClickTerms() {
      this.notificationCreator.create({
        title: this.$t("terms-and-conditions-title"),
        type: "info",
        content: this.$t("terms-and-conditions-message"),
        duration: 10000,
      });
    },
    handleClickPolicy() {
      let url;
      if (this.organizationConfig?.privacyPolicyUrl) {
        url = this.organizationConfig.privacyPolicyUrl;
      } else {
        const routeData = this.$router.resolve({
          name: "policy",
        });
        url = routeData.href;
      }
      window.open(url, "_blank");
    },
    async askForNotifications() {
      if (this.deviceIsIphone) return false;
      this.waitingNotificationsApproval = true;
      let result;
      try {
        result = await Promise.race([
          Notification.requestPermission(),
          new Promise((resolve) => setTimeout(() => resolve("denied"), 5000)),
        ]);
      } catch (e) {
        console.error(e);
      }
      this.waitingNotificationsApproval = false;
      return result === "granted";
    },
    async createCustomer() {
      let notificationsApproved;
      try {
        notificationsApproved = await this.askForNotifications();
      } catch {
        console.log("Notifications not granted");
      }
      let form = this.isCustomSignup
        ? this.$refs.customFormRef
        : this.$refs.legacyFormRef;
      let emailElement = this.signupElements?.find(
        (e) => e.attribute === "email"
      );
      if (emailElement?.hidden) {
        this.signupInfo.email = `${Date.now()}@noemail.comeback.es`;
      }
      let formValid = true;
      try {
        await form.validate();
      } catch {
        formValid = false;
      }
      this.policyError = !this.policyCheck;
      if (!formValid || this.policyError) {
        this.scrollToFirstError();
        return;
      }
      this.loading = true;
      let customerData = {
        ...this.signupInfo,
        birthDate: this.formattedBirthDate,
      };

      let response;
      try {
        response = await api.post(
          `/organization/${this.authStore.organization.id}/createCustomer`,
          {
            customer: customerData,
            source:
              this.$route.query?.source ||
              this.signupPage?.customersSource ||
              "",
            customFields: this.isCustomSignup
              ? this.mappedSelectedCustomFields
              : this.signupInfo.customerCustomFields,
            locationId: this.currentLocation?.id,
            countryCode: this.countryCode,
            deliveryCode: this.deliveryCode,
            referralCode: this.referralsEnabled ? this.referralCode : null,
            giftCardId: this.giftCardId,
            passId: this.authStore.passId,
            signupPage: this.signupPage,
            shouldSendRecoveryEmail:
              !this.deviceIsIphone || !this.$route.query.lastappUrl,
            deviceIsIphone: this.deviceIsIphone,
          }
        );
        let customer = response?.data.customer;
        this.notificationCreator.create({
          title: this.$t("signup-completed"),
          type: "success",
          duration: 3500,
        });
        if (this.appointment) {
          await api.post(
            `/appointments/location/${this.currentLocation.id}/appointment`,
            { ...this.appointment, customerId: customer.id }
          );
          googleConversion.sendConversionEvent();
        }
        if (this.reservation) {
          await api.post(
            `/reservations/location/${this.currentLocation.id}/reservation`,
            { ...this.reservation, customerId: customer.id }
          );
          googleConversion.sendConversionEvent();
        }
        await metaPixel.signupComplete();
        if (!customer.email.includes("@noemail.comeback.es")) {
          this.authStore.updateCurrentCustomer(customer);
        }
        try {
          if (notificationsApproved) {
            await this.handlePushSubscription(customer.id);
          }
        } catch {
          console.error("Failed to subscribe to push notifications");
        }
        if (this.$route.query.lastappUrl) {
          this.redirectToLastapp(customer.phoneNumber);
        } else if (response?.data.gameParticipationId) {
          this.$router.push({
            name: "gamePage",
            params: {
              gameParticipationId: response.data.gameParticipationId,
            },
          });
        } else {
          this.$router.push({
            name: "downloadPage",
          });
        }
      } catch (e) {
        this.handleCreationError(e);
      }
      this.loading = false;
    },
    async handlePushSubscription(customerId) {
      let pushSubscription =
        await this.serviceWorkerRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: import.meta.env
            .VITE_APPLICATION_SERVER_KEY_PUBLIC,
        });
      await api.post(`/customer/${customerId}/savePushSubscription`, {
        pushSubscription,
      });
    },
    handleCreationError(e) {
      if (e.response?.data?.deliveryCodeSuccess) {
        this.notificationCreator.create({
          title: this.$t("points-added-title"),
          type: "success",
          content: this.$t("points-added-message"),
          duration: 3500,
        });
      }
      if (e.response?.status === 409) {
        if (this.$route.query.lastappUrl) {
          if (this.deviceIsIphone) {
            this.dialogCreator.success({
              title: this.$t("account-linked-title"),
              content: this.$t("account-linked-message-1"),
              positiveText: this.$t("continue-ordering"),
              onPositiveClick: () => {
                this.redirectToLastapp(
                  this.$utils.formatNumber(this.signupInfo.phoneNumber)
                );
              },
            });
          } else {
            this.dialogCreator.success({
              title: this.$t("continue-ordering-2"),
              content: this.$t("account-linked-message-2", {
                coveredEmail: e.response.data.coveredEmail,
              }),
              positiveText: this.$t("continue-ordering-1"),
              onPositiveClick: () => {
                this.redirectToLastapp(
                  this.$utils.formatNumber(this.signupInfo.phoneNumber)
                );
              },
            });
          }
        } else {
          if (this.deviceIsIphone) {
            this.$router.push({
              name: "recoveryPage",
              query: {
                ...this.$route.query,
              },
            });
          } else {
            this.dialogCreator.warning({
              title: this.$t("account-already-exists-title"),
              content: this.$t("account-already-exists-message", {
                coveredEmail: e.response.data.coveredEmail,
              }),
              positiveText: this.$t("accept"),
            });
          }
        }
      } else {
        throw e;
      }
    },
  },
};
</script>

<style scoped>
.signup-form :deep(.n-form-item-feedback--error .n-form-item-feedback__line) {
  display: flex;
  align-items: center;
  gap: 4px;
}

.signup-form
  :deep(.n-form-item-feedback--error .n-form-item-feedback__line::before) {
  content: "";
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23DB3D55' d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z'/%3E%3C/svg%3E")
    no-repeat center / contain;
}

.signup-checkbox--error :deep(.n-checkbox-box__border) {
  border: 1.5px solid #db3d55 !important;
}

.signup-form :deep(.vue-tel-input) {
  height: 48px;
  border: 1.5px solid var(--lineColor);
  border-radius: 12px;
  background-color: var(--fieldColor);
  box-shadow: none;
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.signup-form :deep(.vue-tel-input:focus-within) {
  border-color: var(--accentColor);
  background-color: var(--surfaceColor);
  box-shadow: 0 0 0 4px var(--ringColor);
}

.signup-form :deep(.n-form-item-blank--error .vue-tel-input) {
  border-color: #db3d55;
}

.signup-form :deep(.n-form-item-blank--error .vue-tel-input:focus-within) {
  box-shadow: 0 0 0 4px rgba(219, 61, 85, 0.15);
}

.signup-form :deep(.vti__dropdown) {
  padding: 0 8px 0 12px;
  border-right: 1px solid var(--lineColor);
  border-radius: 12px 0 0 12px;
}

.signup-form :deep(.vti__dropdown:hover),
.signup-form :deep(.vti__dropdown.open) {
  background-color: transparent;
}

.signup-form :deep(.vti__input) {
  padding: 0 14px;
  border-radius: 0 12px 12px 0;
  background-color: transparent;
  color: var(--secondaryColor);
  font-size: 15px;
}

.signup-form :deep(.vti__input::placeholder) {
  color: var(--mutedColor);
  opacity: 0.75;
}
</style>
